/**
 * Resmi fatura görünümü (GİB e-Arşiv / e-Fatura şablonu) için veri modeli.
 *
 * Kaynaklar:
 *   - Taslak / EDM'den gönderilmiş fatura → faturaEslestirmeleri kaydı (kalemler dahil, birebir)
 *   - Kolaysoft arşivi → kolaysoftFaturalari kaydı (Excel dökümünde KALEM YOK; özet görünüm)
 *
 * Model, GİB'in standart XSLT çıktısındaki alanları taşır: satıcı / alıcı blokları,
 * özelleştirme–senaryo–tip–no–tarih tablosu, ETTN, kalem tablosu, toplamlar, notlar,
 * "Yalnız #…#" satırı ve QR içeriği.
 */

export const OZEL_MATRAH_805_TEXT =
  '805 - Altından mamül veya altın ihtiva eden ziynet eşyaları ile sikke altınların teslim ve ithali';

const BIRLER = ['', 'Bir', 'İki', 'Üç', 'Dört', 'Beş', 'Altı', 'Yedi', 'Sekiz', 'Dokuz'];
const ONLAR = ['', 'On', 'Yirmi', 'Otuz', 'Kırk', 'Elli', 'Altmış', 'Yetmiş', 'Seksen', 'Doksan'];
const GRUPLAR = ['', 'Bin', 'Milyon', 'Milyar', 'Trilyon'];

const ucHane = (n) => {
  const yuz = Math.floor(n / 100);
  const on = Math.floor((n % 100) / 10);
  const bir = n % 10;
  return `${yuz ? `${yuz === 1 ? '' : BIRLER[yuz]}Yüz` : ''}${ONLAR[on]}${BIRLER[bir]}`;
};

/** 21600 → "YirmiBirBinAltıYüz" (GİB şablonundaki gibi bitişik) */
export const integerToTurkishWords = (value) => {
  let n = Math.floor(Math.abs(Number(value) || 0));
  if (n === 0) return 'Sıfır';
  let out = '';
  let g = 0;
  while (n > 0 && g < GRUPLAR.length) {
    const part = n % 1000;
    if (part) {
      // 1000 "BirBin" değil "Bin"
      const words = g === 1 && part === 1 ? '' : ucHane(part);
      out = `${words}${GRUPLAR[g]}${out}`;
    }
    n = Math.floor(n / 1000);
    g += 1;
  }
  return out;
};

/**
 * 21600 → "#YirmiBirBinAltıYüz TL#", 11200.5 → "#OnBirBinİkiYüz TL, Elli Kr#"
 *
 * Kuruşlu biçim Kolaysoft arşivindeki 11.547 faturayla birebir doğrulandı:
 * ayraç virgül ve birim "Kr" (eskiden boşluk + "Kuruş" yazılıyordu).
 */
export const amountToTurkishWords = (value) => {
  const total = Math.round((Number(value) || 0) * 100);
  const lira = Math.floor(total / 100);
  const kurus = total % 100;
  return kurus
    ? `#${integerToTurkishWords(lira)} TL, ${integerToTurkishWords(kurus)} Kr#`
    : `#${integerToTurkishWords(lira)} TL#`;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const digits = (v) => String(v || '').replace(/\D/g, '');

/** "2026-09-14" → "14-09-2026" */
const trDate = (day) => {
  const [y, m, d] = String(day || '').slice(0, 10).split('-');
  return y && m && d ? `${d}-${m}-${y}` : '';
};

const idLabel = (id) => (digits(id).length === 10 ? 'VKN' : 'TCKN');

const sellerFromSettings = (settings = {}, fallback = {}) => {
  const taxId = digits(settings.taxNumber || fallback.id);
  const title = String(settings.companyTitle || fallback.title || '').trim();
  return {
    title,
    address: String(settings.address || '').trim(),
    district: String(settings.district || '').trim(),
    city: String(settings.city || 'İstanbul').trim(),
    taxOffice: String(settings.taxOffice || '').trim(),
    id: taxId,
    idLabel: idLabel(taxId),
    tradeRegistry: String(settings.tradeRegistryNo || settings.ticaretSicilNo || '').trim() || '-',
    mersisNo: String(settings.mersisNo || '').trim()
  };
};

const scenarioCode = (documentType) => (documentType === 'EFATURA' ? 'TEMELFATURA' : 'EARSIVFATURA');

/**
 * Taslaktan (veya EDM ile gönderilmiş kayıttan) model.
 * @param {{ match: object, form: object, settings?: object }} args
 */
export const buildOfficialModelFromDraft = ({ match = {}, form = {}, settings = {} }) => {
  const documentType =
    String(match.documentType || match.providerRef?.documentType || '').toUpperCase() === 'EFATURA' ||
    form.scenarioType === 'eFatura'
      ? 'EFATURA'
      : 'EARSIV';
  const isOzelMatrah = (form.invoiceType || 'ozelMatrah') === 'ozelMatrah';
  const items = Array.isArray(form.items) ? form.items : [];
  const totals = form.invoiceTotals || {};

  const lines = items.map((it, i) => {
    const lineTotal = round2(it.lineTotal);
    const vat = round2(it.vatAmount);
    const net = round2(lineTotal - vat);
    const isGold = isOzelMatrah && Number(it.vatRate) === 0;
    return {
      no: i + 1,
      code: String(it.productName || '').trim(),
      name: String(it.productName || '').trim(),
      quantity: Number(it.quantity) || 0,
      unit: String(it.unit || 'ADET').toUpperCase() === 'GRAM' ? 'Gram' : 'Adet',
      unitPrice: Number(it.unitPrice) || 0,
      vatRate: Number(it.vatRate) || 0,
      vatAmount: vat,
      net,
      // GİB şablonunda özel matrah nedeni has (KDV %0) satırında görünür.
      // Tutar O SATIRIN kendi matrahidir (= satir neti), faturanin toplam
      // isciligi DEGIL: coklu faturada her altin satirina ayni rakam
      // yaziliyordu. Olcut arsivdeki 10.903 satir ve urettigimiz UBL'deki
      // <cbc:TaxableAmount> — ikisi de satir netini tasiyor.
      ozelMatrahReason: isGold ? String(it.ozelMatrahCode || '805') : '',
      ozelMatrahAmount: isGold ? net : null
    };
  });

  const goodsTotal = round2(lines.reduce((s, l) => s + l.net, 0));
  const vatByRate = {};
  lines.forEach((l) => {
    vatByRate[l.vatRate] = round2((vatByRate[l.vatRate] || 0) + l.vatAmount);
  });
  const taxInclusive = round2(totals.taxInclusiveAmount || goodsTotal + Object.values(vatByRate).reduce((a, b) => a + b, 0));
  const payable = round2(totals.payableAmount || taxInclusive);

  const issued = Boolean(match.providerRef?.invoiceId || match.providerRef?.uuid) && ['sent', 'approved'].includes(match.invoiceStatus);

  const notes = String(form.invoiceNote || '')
    .split('\n')
    .map((n) => n.trim())
    .filter(Boolean);

  return {
    source: 'draft',
    issued,
    watermark: issued ? '' : 'TASLAK',
    documentType,
    seller: sellerFromSettings(settings),
    buyer: {
      title: String(form.title || form.customerName || '').trim(),
      address: String(form.address || form.customerAddress || '').trim(),
      district: String(form.district || form.customerIlce || '').trim(),
      city: String(form.city || form.customerIl || '').trim(),
      country: String(form.country || 'Türkiye').trim(),
      taxOffice: String(form.taxOffice || form.customerTaxOffice || '').trim(),
      id: digits(form.vknTckn),
      idLabel: idLabel(form.vknTckn)
    },
    meta: {
      customizationId: 'TR1.2',
      scenario: scenarioCode(documentType),
      invoiceType: isOzelMatrah ? 'OZELMATRAH' : 'SATIS',
      number: match.providerRef?.invoiceId || form.invoiceNumber || '',
      date: trDate(form.invoiceDate),
      time: String(form.invoiceTime || '').slice(0, 5)
    },
    ettn: match.providerRef?.uuid || '',
    lines,
    linesMissing: false,
    totals: {
      goodsTotal,
      discount: round2(totals.totalDiscount),
      vat0: vatByRate[0] || 0,
      vat20: vatByRate[20] || 0,
      taxInclusive,
      payable
    },
    exemptionText: isOzelMatrah ? OZEL_MATRAH_805_TEXT : '',
    notes,
    amountWords: amountToTurkishWords(payable)
  };
};

const UBL_UNIT_LABELS = { GRM: 'Gram', C62: 'Adet', KGM: 'Kg', NIU: 'Adet' };

/**
 * Kolaysoft XML (UBL) dökümünden okunmuş fatura → kalemli, birebir model.
 * GİB şablonu notları "Not:" önekiyle gösterir; "Yalnız #…#" satırı amountWords'ten gelir.
 */
const buildOfficialModelFromUbl = (doc, ubl) => {
  const documentType = String(ubl.profileId || '').toUpperCase() === 'EARSIVFATURA' ? 'EARSIV' : 'EFATURA';
  const party = (p = {}) => {
    const id = digits(p.id);
    return {
      title: String(p.title || '').trim(),
      address: String(p.street || '').trim(),
      district: String(p.district || '').trim(),
      city: String(p.city || '').trim(),
      country: String(p.country || 'Türkiye').trim(),
      taxOffice: String(p.taxOffice || '').trim(),
      id,
      idLabel: p.idScheme === 'VKN' || p.idScheme === 'TCKN' ? p.idScheme : idLabel(id),
      tradeRegistry: String(p.tradeRegistry || '').trim() || '-',
      mersisNo: String(p.mersisNo || '').trim()
    };
  };
  const subtotals = Array.isArray(ubl.taxSubtotals) ? ubl.taxSubtotals : [];
  const vatOf = (rate) => round2(subtotals.filter((t) => Number(t.percent) === rate).reduce((a, t) => a + (Number(t.tax) || 0), 0));
  const exemption = subtotals.find((t) => t.exemptionCode);
  const totals = ubl.totals || {};
  const payable = round2(totals.payable || doc.total);
  return {
    source: 'kolaysoft',
    issued: true,
    watermark: '',
    documentType,
    seller: party(ubl.supplier),
    buyer: party(ubl.customer),
    meta: {
      customizationId: String(ubl.customizationId || 'TR1.2'),
      scenario: String(ubl.profileId || doc.scenario || ''),
      invoiceType: String(ubl.invoiceTypeCode || doc.invoiceType || ''),
      number: String(doc.invoiceNumber || ''),
      date: trDate(ubl.issueDate || doc.invoiceDate),
      time: String(ubl.issueTime || '').slice(0, 5)
    },
    ettn: String(doc.ettn || ''),
    lines: (ubl.lines || []).map((l, i) => {
      const is805 = String(l.exemptionCode || '') === '805';
      return {
        no: Number(l.no) || i + 1,
        code: String(l.code || l.name || '').trim(),
        name: String(l.name || '').trim(),
        quantity: Number(l.quantity) || 0,
        unit: UBL_UNIT_LABELS[l.unitCode] || String(l.unitCode || ''),
        unitPrice: Number(l.unitPrice) || 0,
        vatRate: Number(l.vatRate) || 0,
        vatAmount: round2(l.vatAmount),
        net: round2(l.lineExtension),
        ozelMatrahReason: is805 ? '805' : '',
        ozelMatrahAmount: is805 ? round2(l.taxableAmount) : null
      };
    }),
    linesMissing: false,
    totals: {
      goodsTotal: round2(totals.lineExtension),
      discount: round2(totals.allowance),
      vat0: vatOf(0),
      vat20: vatOf(20),
      taxInclusive: round2(totals.taxInclusive),
      payable
    },
    exemptionText: exemption ? `${exemption.exemptionCode} - ${exemption.exemptionReason || ''}`.trim() : '',
    notes: (ubl.notes || [])
      .map((n) => String(n || '').trim())
      .filter((n) => n && !/^Yalnız\s*#/i.test(n))
      .map((n) => (/^Not\s*:/i.test(n) ? n : `Not:${n}`)),
    amountWords: amountToTurkishWords(payable)
  };
};

/** EDM'den gelen faturanın UBL'i → resmi görünüm (satıcı karşı firma, alıcı biz). */
export const buildOfficialModelFromInbound = (row = {}, ubl = {}) => ({
  ...buildOfficialModelFromUbl(
    {
      invoiceNumber: ubl.invoiceNumber || row.invoiceId || '',
      ettn: ubl.ettn || row.uuid || '',
      total: row.payableAmount,
      invoiceDate: row.issueDate
    },
    ubl
  ),
  source: 'edm-inbound'
});

/** Kolaysoft arşiv kaydından model: XML (ubl) işlenmişse birebir, yoksa Excel özeti (kalemsiz). */
export const buildOfficialModelFromArchive = (doc = {}, settings = {}) => {
  if (doc.ubl && Array.isArray(doc.ubl.lines) && doc.ubl.lines.length) return buildOfficialModelFromUbl(doc, doc.ubl);
  const documentType = String(doc.documentType || '').toUpperCase() === 'EFATURA' ? 'EFATURA' : 'EARSIV';
  const taxExclusive = round2(doc.taxExclusive || doc.goodsTotal);
  const taxInclusive = round2(doc.taxInclusive || doc.total);
  const vat20 = round2(Math.max(0, taxInclusive - taxExclusive));
  const recipientId = digits(doc.recipientId);
  return {
    source: 'kolaysoft',
    issued: true,
    watermark: '',
    documentType,
    // Ayarlardaki satıcı başka bir VKN ise (yanlış şube ayarı) Kolaysoft dökümündeki gönderen kullanılır
    seller:
      !doc.senderId || digits(settings.taxNumber) === digits(doc.senderId)
        ? sellerFromSettings(settings, { id: doc.senderId, title: doc.senderTitle })
        : sellerFromSettings(
            { city: settings.city, taxOffice: settings.taxOffice },
            { id: doc.senderId, title: doc.senderTitle }
          ),
    buyer: {
      title: String(doc.recipientTitle || '').trim(),
      address: '',
      district: '',
      city: '',
      country: 'Türkiye',
      taxOffice: '',
      id: recipientId,
      idLabel: idLabel(recipientId)
    },
    meta: {
      customizationId: 'TR1.2',
      scenario: String(doc.scenario || scenarioCode(documentType)),
      invoiceType: String(doc.invoiceType || 'OZELMATRAH'),
      number: String(doc.invoiceNumber || ''),
      date: trDate(doc.invoiceDate),
      time: ''
    },
    ettn: String(doc.ettn || ''),
    lines: [],
    linesMissing: true,
    totals: {
      goodsTotal: round2(doc.goodsTotal || taxExclusive),
      discount: round2(doc.discount),
      vat0: 0,
      vat20,
      taxInclusive,
      payable: round2(doc.total || taxInclusive)
    },
    exemptionText: String(doc.invoiceType || 'OZELMATRAH') === 'OZELMATRAH' ? OZEL_MATRAH_805_TEXT : '',
    notes: [],
    amountWords: amountToTurkishWords(doc.total || taxInclusive)
  };
};

/**
 * Faturalar modülündeki bir kayıt için doğru modeli seçer.
 *  - Kolaysoft'tan kesilmiş kayıt: taslağın kalemleri varsa ve tutarı arşivdeki fatura ile
 *    (±1 TL) tutuyorsa kalemli görünüm + Kolaysoft no/ETTN/tarih; aksi halde arşiv özeti.
 *  - Diğerleri: taslak / EDM kaydı.
 */
export const buildOfficialModelForMatch = ({ match = {}, form = {}, settings = {}, archiveDoc = null }) => {
  const ext = match.externalInvoice;
  if (ext?.provider !== 'kolaysoft') return buildOfficialModelFromDraft({ match, form, settings });

  const archive = archiveDoc || {
    ettn: ext.ettn,
    invoiceNumber: ext.invoiceNumber,
    invoiceDate: ext.invoiceDate,
    recipientId: ext.recipientId,
    recipientTitle: ext.recipientTitle,
    total: ext.total,
    taxInclusive: ext.total,
    documentType: ext.documentType
  };
  // XML'i işlenmiş Kolaysoft faturası: kesilen belgenin kendisi, taslağa bakılmaz
  if (archiveDoc?.ubl?.lines?.length) return buildOfficialModelFromArchive(archiveDoc, settings);
  const archiveTotal = round2(archive.total || archive.taxInclusive);
  const hasLines = Array.isArray(form.items) && form.items.some((it) => Number(it.lineTotal) > 0);
  const draftModel = hasLines ? buildOfficialModelFromDraft({ match, form, settings }) : null;
  if (!draftModel || Math.abs(draftModel.totals.payable - archiveTotal) > 1) {
    return buildOfficialModelFromArchive(archive, settings);
  }
  const archiveModel = buildOfficialModelFromArchive(archive, settings);
  return {
    ...draftModel,
    source: 'kolaysoft',
    issued: true,
    watermark: '',
    documentType: archiveModel.documentType,
    seller: archiveModel.seller,
    buyer: {
      ...draftModel.buyer,
      title: draftModel.buyer.title || archiveModel.buyer.title,
      id: draftModel.buyer.id || archiveModel.buyer.id,
      idLabel: draftModel.buyer.id ? draftModel.buyer.idLabel : archiveModel.buyer.idLabel
    },
    meta: {
      ...draftModel.meta,
      scenario: archiveModel.meta.scenario,
      invoiceType: archiveModel.meta.invoiceType || draftModel.meta.invoiceType,
      number: archiveModel.meta.number,
      date: archiveModel.meta.date || draftModel.meta.date
    },
    ettn: archiveModel.ettn
  };
};

/**
 * GİB karekod içeriği (e-Arşiv / e-Fatura görüntüsündeki QR).
 * Numara ve ETTN yoksa (taslak) karekod üretilmez.
 */
export const buildGibQrPayload = (model) => {
  if (!model?.meta?.number || !model?.ettn) return '';
  const fmt = (n) => round2(n).toFixed(2);
  const [d, m, y] = String(model.meta.date || '').split('-');
  const payload = {
    vkntckn: model.seller.id,
    avkntckn: model.buyer.id,
    senaryo: model.meta.scenario,
    tip: model.meta.invoiceType,
    tarih: y && m && d ? `${y}-${m}-${d}` : '',
    no: model.meta.number,
    ettn: model.ettn,
    parabirimi: 'TRY',
    malhizmettoplam: fmt(model.totals.goodsTotal),
    'kdvmatrah(0)': fmt(model.totals.goodsTotal - (model.totals.vat20 ? model.totals.vat20 / 0.2 : 0)),
    'hesaplanankdv(0)': fmt(model.totals.vat0),
    'kdvmatrah(20)': fmt(model.totals.vat20 ? model.totals.vat20 / 0.2 : 0),
    'hesaplanankdv(20)': fmt(model.totals.vat20),
    vergidahil: fmt(model.totals.taxInclusive),
    odenecek: fmt(model.totals.payable)
  };
  return JSON.stringify(payload);
};
